import { TankInput } from '@tankworld/shared';
import { useGameStore } from '../state/game-store.js';

export class InputManager {
  private keys: Record<string, boolean> = {};
  private turretYaw = 0;
  private gunPitch = 0;
  private sequence = 1;
  private wantsToFire = false;
  private isPointerLocked = false;

  constructor() {
    this.bindEvents();
  }

  private bindEvents(): void {
    window.addEventListener('keydown', (e) => {
      this.keys[e.code] = true;

      if (e.code === 'KeyC') {
        useGameStore.getState().cycleCameraMode();
      }

      if (e.code === 'Escape') {
        const store = useGameStore.getState();
        if (store.gameMode !== 'menu') {
          store.setPaused(!store.isPaused);
        }
      }

      if (e.code === 'Space') {
        this.wantsToFire = true;
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys[e.code] = false;
    });

    // Window focus loss safety: release all keys immediately!
    window.addEventListener('blur', () => {
      this.keys = {};
      this.wantsToFire = false;
    });

    // Pointer lock for desktop mouse aim
    window.addEventListener('mousemove', (e) => {
      const store = useGameStore.getState();
      if (store.isPaused || store.gameMode === 'menu') return;

      const sensitivity = 0.0022 * store.settings.mouseSensitivity;
      const invert = store.settings.invertY ? -1 : 1;

      this.turretYaw += e.movementX * sensitivity;
      this.gunPitch += -e.movementY * sensitivity * invert;

      // Clamp gun pitch to allowable vehicle limits
      this.gunPitch = Math.max(-0.14, Math.min(0.35, this.gunPitch));
    });

    window.addEventListener('mousedown', (e) => {
      const store = useGameStore.getState();
      if (store.isPaused || store.gameMode === 'menu') return;

      if (e.button === 0) { // Left click
        this.wantsToFire = true;
      }
    });

    document.addEventListener('pointerlockchange', () => {
      this.isPointerLocked = document.pointerLockElement !== null;
    });
  }

  public requestPointerLock(element: HTMLElement): void {
    if (!this.isPointerLocked) {
      element.requestPointerLock?.();
    }
  }

  public releasePointerLock(): void {
    if (document.pointerLockElement) {
      document.exitPointerLock?.();
    }
  }

  // Virtual Touch Controls
  private virtualThrottle = 0;
  private virtualSteer = 0;
  private virtualBrake = false;

  public setVirtualMove(throttle: number, steer: number): void {
    this.virtualThrottle = Math.max(-1.0, Math.min(1.0, throttle));
    this.virtualSteer = Math.max(-1.0, Math.min(1.0, steer));
  }

  public setVirtualBrake(brake: boolean): void {
    this.virtualBrake = brake;
  }

  public triggerVirtualFire(): void {
    this.wantsToFire = true;
  }

  public applyVirtualAimDelta(deltaX: number, deltaY: number): void {
    const store = useGameStore.getState();
    const sensitivity = 0.0035 * store.settings.mouseSensitivity;
    const invert = store.settings.invertY ? -1 : 1;

    this.turretYaw += deltaX * sensitivity;
    this.gunPitch += -deltaY * sensitivity * invert;

    // Clamp gun pitch to allowable vehicle limits
    this.gunPitch = Math.max(-0.14, Math.min(0.35, this.gunPitch));
  }

  public resetVirtualInputs(): void {
    this.virtualThrottle = 0;
    this.virtualSteer = 0;
    this.virtualBrake = false;
  }

  public getTurretAngles(): { yaw: number; pitch: number } {
    return { yaw: this.turretYaw, pitch: this.gunPitch };
  }

  public setTurretYaw(yaw: number): void {
    this.turretYaw = yaw;
  }

  public pollInput(): { input: TankInput; wantsToFire: boolean } {
    let throttle = this.virtualThrottle;
    if (this.keys['KeyW'] || this.keys['ArrowUp']) throttle += 1.0;
    if (this.keys['KeyS'] || this.keys['ArrowDown']) throttle -= 1.0;
    throttle = Math.max(-1.0, Math.min(1.0, throttle));

    let steer = this.virtualSteer;
    if (this.keys['KeyA'] || this.keys['ArrowLeft']) steer -= 1.0;
    if (this.keys['KeyD'] || this.keys['ArrowRight']) steer += 1.0;
    steer = Math.max(-1.0, Math.min(1.0, steer));

    const brake = this.virtualBrake || Boolean(this.keys['Space'] && throttle === 0 && steer === 0);
    const fire = this.wantsToFire;
    this.wantsToFire = false;

    const input: TankInput = {
      sequence: this.sequence++,
      throttle,
      steer,
      brake,
      turretYaw: this.turretYaw,
      gunPitch: this.gunPitch,
    };

    return { input, wantsToFire: fire };
  }
}


export const inputManager = new InputManager();
